// Configure the generated singleton with the app's API base URL and cookie auth.
import './api';
import {
  getQuestionCustomizations as getQuestionCustomizationsRequest,
  getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet,
  getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost,
  replaceQuestionCustomizations as replaceQuestionCustomizationsRequest,
} from './api/sdk.gen';
import type {
  QuestionCustomizationsQuestion,
  QuestionCustomizationsResponse,
  RogerDocFormTypeCatalogEntry,
} from './api/types.gen';

export type { QuestionCustomizationsQuestion, QuestionCustomizationsResponse };

export type QuestionTaxonomyKey = 'oasis-assessments';

export type QuestionTaxonomy = {
  key: QuestionTaxonomyKey;
  label: string;
  description: string;
};

export const QUESTION_TAXONOMIES: readonly QuestionTaxonomy[] = [
  {
    key: 'oasis-assessments',
    label: 'OASIS assessments',
    description: 'Start, follow-up, transfer, and end-of-care assessments.',
  },
] as const;

const TAXONOMY_BY_KEY = new Map(
  QUESTION_TAXONOMIES.map(taxonomy => [taxonomy.key, taxonomy])
);

const OASIS_FORM_TYPES = new Set([
  'SOC',
  'ROC',
  'REC',
  'DC',
  'SOCPT',
  'ROCPT',
  'RECPT',
  'DCPT',
  'SOCOT',
  'ROCOT',
  'RECOT',
  'DCOT',
  'SOCST',
  'ROCST',
  'RECST',
  'DCST',
  'FUP',
  'FUPPT',
  'FUPOT',
  'FUPST',
  'TF',
  'TFPT',
  'TFOT',
  'TFST',
  'DD',
  'DDPT',
  'DDOT',
  'DDST',
]);

/** Map each current RogerDoc form family to the documentation surface it serves. */
export function taxonomyForFormType(formType: string): QuestionTaxonomy | null {
  return OASIS_FORM_TYPES.has(formType)
    ? (TAXONOMY_BY_KEY.get('oasis-assessments') ?? null)
    : null;
}

export type QuestionUsageSource = {
  formType: string;
  label: string;
  /** Broad documentation surface for this form family. */
  family: string;
  taxonomyKey: QuestionTaxonomyKey;
  taxonomyLabel: string;
  taxonomyDescription: string;
  /** Human-readable form + documentation surface context for shared QIDs. */
  contextLabel: string;
};

export type QuestionUsage = ReadonlyMap<string, readonly QuestionUsageSource[]>;

export type QuestionUsageResult = {
  usage: QuestionUsage;
  hasMetadataFailure: boolean;
};

type SchemaNode = { qid?: unknown };

type VersionedSchemaPayload = {
  schema_data?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function collectQids(value: unknown, qids: Set<string>): void {
  if (Array.isArray(value)) {
    value.forEach(item => collectQids(item, qids));
    return;
  }
  if (!isRecord(value)) return;

  const node = value as SchemaNode;
  if (typeof node.qid === 'string' && node.qid.trim()) qids.add(node.qid);
  Object.entries(value).forEach(([key, child]) => {
    if (key !== 'qid') collectQids(child, qids);
  });
}

function formLabel(entry: RogerDocFormTypeCatalogEntry): string {
  const [, label] = entry.display_label.split('—', 2);
  return (label ?? entry.display_label).trim() || entry.form_type;
}

/** Extract QIDs from one schema value and retain its form context. */
export function collectQuestionUsage(
  schema: unknown,
  source: QuestionUsageSource,
  usage: Map<string, QuestionUsageSource[]>
): void {
  const qids = new Set<string>();
  collectQids(schema, qids);
  qids.forEach(qid => {
    const sources = usage.get(qid) ?? [];
    const alreadyRecorded = sources.some(
      item => item.formType === source.formType
    );
    if (!alreadyRecorded) usage.set(qid, [...sources, source]);
  });
}

function collectVersionedSchemaUsage(
  payload: unknown,
  entry: RogerDocFormTypeCatalogEntry,
  usage: Map<string, QuestionUsageSource[]>
): boolean {
  if (!isRecord(payload)) return false;
  const versioned = payload as VersionedSchemaPayload;
  if (!isRecord(versioned.schema_data)) return false;

  const taxonomy = taxonomyForFormType(entry.form_type);
  if (!taxonomy) return false;
  collectQuestionUsage(
    versioned.schema_data,
    {
      formType: entry.form_type,
      label: formLabel(entry),
      family: taxonomy.label,
      taxonomyKey: taxonomy.key,
      taxonomyLabel: taxonomy.label,
      taxonomyDescription: taxonomy.description,
      contextLabel: `${formLabel(entry)} · ${taxonomy.label}`,
    },
    usage
  );
  return true;
}

/**
 * Read each currently served EMR schema to find the form families that use a
 * catalog question. Taxonomy comes from RogerEMR's form-family and visit
 * semantics. The schema tree supplies QID evidence only; its section names
 * never become catalog groups.
 */
export async function getQuestionUsage(): Promise<QuestionUsageResult> {
  const catalogResponse =
    await getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet({
      throwOnError: true,
    });

  const oasisEntries = catalogResponse.data.filter(entry =>
    OASIS_FORM_TYPES.has(entry.form_type)
  );

  const results = await Promise.all(
    oasisEntries.map(async entry => {
      try {
        const response =
          await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
            query: { visit_type: entry.canonical_visit_type_id },
            throwOnError: true,
          });
        return {
          entry,
          payload: response.data,
          success:
            isRecord(response.data) && isRecord(response.data.schema_data),
        };
      } catch (error) {
        console.error(
          `Failed to load schema metadata for ${entry.form_type}:`,
          error
        );
        return { entry, payload: null, success: false };
      }
    })
  );

  const usage = new Map<string, QuestionUsageSource[]>();
  const supportedResults = results.map(result => ({
    ...result,
    supported:
      result.success && taxonomyForFormType(result.entry.form_type) !== null,
  }));
  supportedResults.forEach(result => {
    if (result.supported) {
      collectVersionedSchemaUsage(result.payload, result.entry, usage);
    }
  });
  return {
    usage,
    hasMetadataFailure: supportedResults.some(result => !result.supported),
  };
}

/** Load the complete catalog and current agency selection. */
export async function getQuestionCustomizations(): Promise<QuestionCustomizationsResponse> {
  const response = await getQuestionCustomizationsRequest({
    throwOnError: true,
  });
  return response.data;
}

/** Replace the current agency's ordered additions and per-question guidance.
 *
 * QuestionCustomizations omits the moreContextByQid parameter to preserve
 * existing guidance on the backend without triggering 403 Forbidden errors.
 */
export async function saveQuestionCustomizations(
  additionQids: string[],
  moreContextByQid?: Record<string, string>
): Promise<QuestionCustomizationsResponse> {
  const body: {
    addition_qids: string[];
    more_context_by_qid?: Record<string, string>;
  } = {
    addition_qids: additionQids,
  };
  if (moreContextByQid !== undefined) {
    body.more_context_by_qid = moreContextByQid;
  }
  const response = await replaceQuestionCustomizationsRequest({
    body,
    throwOnError: true,
  });
  return response.data;
}
